import { useEffect, useRef, useState } from 'react';

/**
 * Track whether a DOM element has scrolled into view (or close to it),
 * with a "fallback enable after delay" so queries don't get stuck in a
 * loading-skeleton state if the IntersectionObserver doesn't fire (e.g.
 * the element is already in view at mount with no scroll event, or the
 * user opens the page already scrolled, or the observer's threshold
 * isn't satisfied due to layout quirks).
 *
 * Returns a [ref, isEnabled] tuple. `isEnabled` flips to `true` either:
 *   - the first time the element intersects the viewport (within
 *     `rootMargin`), OR
 *   - after `fallbackDelayMs` from mount, whichever comes first
 * and STAYS true — we don't flip back to false on scroll-away.
 *
 * The fallback addresses a real bug we hit: pure visibility-gating made
 * below-fold widgets stuck on the loading skeleton because the queries
 * never fired until scroll. With the fallback, the worst case is "data
 * ready by the time the user scrolls" and the best case is "data ready
 * before the user scrolls because they didn't reach it in 400ms".
 *
 * @param {Object} options
 * @param {string} options.rootMargin - CSS margin string for prefetch headroom.
 * @param {number} options.threshold - 0–1, fraction of element visible to trigger.
 * @param {number} options.fallbackDelayMs - Time after mount to enable
 *   regardless of visibility. Set to `0` for pure-time deferral or
 *   `Infinity` for pure-visibility gating. Default 400ms is a reasonable
 *   "let above-the-fold paint first, then start below-the-fold loads".
 */
export function useInView({
  rootMargin = '200px',
  threshold = 0.01,
  fallbackDelayMs = 400,
} = {}) {
  const ref = useRef(null);
  const [isEnabled, setIsEnabled] = useState(false);

  // Visibility-based enable
  useEffect(() => {
    if (isEnabled) return; // Once enabled, stay enabled
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Old browser fallback: just enable.
      setIsEnabled(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsEnabled(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isEnabled, rootMargin, threshold]);

  // Time-based fallback enable
  useEffect(() => {
    if (isEnabled) return;
    if (fallbackDelayMs === Infinity || fallbackDelayMs < 0) return;
    const timer = setTimeout(() => setIsEnabled(true), fallbackDelayMs);
    return () => clearTimeout(timer);
  }, [isEnabled, fallbackDelayMs]);

  return [ref, isEnabled];
}
